import { useState, useEffect, useMemo } from 'react';
import { FlightEntity } from '@src/features/order/entities';
import { getFlightHeading } from '@src/features/order/provider/flight-provider/flight-position-cache';

function parseHeadingField(raw: unknown): number | undefined {
  if (typeof raw === 'number' && !Number.isNaN(raw)) return raw;
  if (typeof raw === 'string' && raw !== '') {
    const n = Number(raw);
    if (!Number.isNaN(n)) return n;
  }
  return undefined;
}

/** Heading per serial from FLIGHT_DATA + module cache so remounts don't flash 0° before the next tick. */
export const useDroneRotations = (
  flightData: FlightEntity | undefined | null
): { rotations: Record<string, number> } => {
  const [rotations, setRotations] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!flightData?.serialNumber) return;

    const serial = flightData.serialNumber as string;
    const live = parseHeadingField(flightData.heading);
    if (live !== undefined) {
      const nextHeading = Math.round(live);
      setRotations(prev => (prev[serial] === nextHeading ? prev : { ...prev, [serial]: nextHeading }));
    }
  }, [flightData]);

  const rotationsMerged = useMemo(() => {
    const out = { ...rotations };
    const serial = flightData?.serialNumber as string | undefined;
    if (!serial) return out;

    const live = parseHeadingField(flightData?.heading);
    const cached = getFlightHeading(serial);
    const resolved = live ?? cached ?? out[serial];
    if (typeof resolved === 'number' && !Number.isNaN(resolved)) {
      out[serial] = resolved;
    }
    return out;
  }, [rotations, flightData]);

  return { rotations: rotationsMerged };
};
